// src/features/merchantConfig/components/EditToolbar.jsx
//
// Footer toolbar that lives at the bottom of the detail panel. In view mode
// it shows an "Edit" button (the entry point into edit mode). In edit mode
// it shows a dirty-count badge + Cancel + Save buttons, and a server-error
// banner if the latest PATCH failed.
//
// Sticky-positioned inside the detail panel so it remains visible even if
// the merchant scrolls a long value block.

import React from "react";
import { Pencil, Save, X, AlertCircle, Loader2 } from "lucide-react";

import { useEditState } from "../state/EditStateContext";
import { humanizeErrorCode } from "../lib/errorMessages";
import { isCategoryEditable } from "../lib/editFieldPermissions";

/**
 * @param {{
 *   canEnter: boolean,                       // disabled when 404 / 5xx / loading
 *   isFetching: boolean,
 *   onSave: () => void,
 *   onCancel: () => void,
 *   onEnter: () => void,
 *   configKey?: string,                       // active category enum, drives allowlist gating
 * }} props
 */
export default function EditToolbar({
  canEnter,
  isFetching,
  onSave,
  onCancel,
  onEnter,
  configKey,
}) {
  const {
    mode,
    isDirty,
    dirtyKeys,
    fieldErrors,
    serverError,
    isSaving,
  } = useEditState();

  if (mode === "view") {
    // Hide the entire toolbar (incl. the Edit button) when the category
    // isn't on the merchant allowlist. Defense-in-depth: the canEnter prop
    // is also gated upstream, so this is redundant - but it keeps the
    // footer chrome from rendering at all on read-only categories.
    if (!isCategoryEditable(configKey)) return null;

    return (
      <div className="sticky bottom-3 z-10 -mx-1 mt-3 flex justify-end">
        <button
          type="button"
          onClick={onEnter}
          disabled={!canEnter || isFetching}
          className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 shadow-sm hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
          Edit configuration
        </button>
      </div>
    );
  }

  const hasFieldErrors = fieldErrors.size > 0;
  const cantSaveReason = !isDirty
    ? "No changes to save"
    : hasFieldErrors
    ? "Fix the highlighted fields before saving"
    : null;

  return (
    <div className="sticky bottom-3 z-10 -mx-1 mt-3 space-y-2">
      {serverError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm">
          <div className="flex items-start gap-2 text-red-900">
            <AlertCircle
              className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
              aria-hidden="true"
            />
            <div className="space-y-0.5">
              <p className="font-semibold">
                {humanizeErrorCode(serverError.error_code)}
              </p>
              <p className="text-red-800">
                {serverError.error_message || "The server rejected the change."}
              </p>
              {serverError.error_context?.field_name && (
                <p className="text-xs text-red-700">
                  Field: <code>{serverError.error_context.field_name}</code>
                  {serverError.error_context.reason && (
                    <> · {serverError.error_context.reason}</>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 shadow-sm">
        <div className="flex items-center gap-3 text-sm">
          {isDirty ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {dirtyKeys.size} unsaved{" "}
              {dirtyKeys.size === 1 ? "change" : "changes"}
            </span>
          ) : (
            <span className="text-xs text-gray-500">No changes yet</span>
          )}
          {hasFieldErrors && (
            <span className="inline-flex items-center gap-1.5 text-xs text-red-700">
              <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
              {fieldErrors.size} field{fieldErrors.size === 1 ? "" : "s"} need
              attention
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
          >
            <X className="h-4 w-4" aria-hidden="true" />
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={Boolean(cantSaveReason) || isSaving}
            title={cantSaveReason ?? undefined}
            className="inline-flex items-center gap-1.5 rounded-md border border-transparent bg-blue-600 px-3.5 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Saving…
              </>
            ) : (
              <>
                <Save className="h-4 w-4" aria-hidden="true" />
                Save changes
                {isDirty ? ` (${dirtyKeys.size})` : ""}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
